import type { ClassifiedNode, NodePosition, NodeState } from "./types";

const stateOrder: NodeState[] = [
  "selected",
  "explore",
  "conditional",
  "review",
  "unknown",
  "available",
  "blocked",
];

function ringPositions(
  count: number,
  centerX: number,
  centerY: number,
  radiusX: number,
  radiusY: number,
  phase = -Math.PI / 2,
): NodePosition[] {
  if (count === 0) return [];
  if (count === 1) return [{ x: centerX, y: centerY - radiusY }];
  return Array.from({ length: count }, (_, index) => {
    const angle = phase + (Math.PI * 2 * index) / count;
    return {
      x: centerX + Math.cos(angle) * radiusX,
      y: centerY + Math.sin(angle) * radiusY,
    };
  });
}

export function buildArcPath(
  start: NodePosition,
  end: NodePosition,
  center: NodePosition,
  minimumRadius = 140,
  steps = 7,
): NodePosition[] {
  const startX = start.x - center.x;
  const startY = start.y - center.y;
  const endX = end.x - center.x;
  const endY = end.y - center.y;
  const startRadius = Math.hypot(startX, startY);
  const endRadius = Math.hypot(endX, endY);
  const startAngle = Math.atan2(startY, startX);
  const endAngle = Math.atan2(endY, endX);
  let angleDelta = endAngle - startAngle;
  while (angleDelta > Math.PI) angleDelta -= Math.PI * 2;
  while (angleDelta < -Math.PI) angleDelta += Math.PI * 2;
  const count = Math.max(3, Math.floor(steps));

  return Array.from({ length: count }, (_, index) => {
    if (index === 0) return { ...start };
    if (index === count - 1) return { ...end };
    const progress = index / (count - 1);
    const radius = Math.max(
      minimumRadius,
      startRadius + (endRadius - startRadius) * progress,
    );
    const angle = startAngle + angleDelta * progress;
    return {
      x: center.x + Math.cos(angle) * radius,
      y: center.y + Math.sin(angle) * radius,
    };
  });
}

function resolveCollisions(
  classified: ClassifiedNode[],
  source: Map<string, NodePosition>,
  width: number,
  height: number,
): Map<string, NodePosition> {
  const positions = new Map<string, NodePosition>();
  const nodeWidth = width <= 520 ? 96 : 124;
  const nodeHeight = width <= 520 ? 96 : 92;
  const gap = 10;
  const halfWidth = nodeWidth / 2;
  const halfHeight = nodeHeight / 2;
  const priority: Record<NodeState, number> = {
    selected: 0,
    blocked: 1,
    review: 2,
    explore: 3,
    conditional: 4,
    unknown: 5,
    available: 6,
  };
  const items = [...classified].sort((left, right) =>
    priority[left.state] - priority[right.state] ||
    left.node.id.localeCompare(right.node.id, "en"),
  );
  const clamp = (position: NodePosition): NodePosition => ({
    x: Math.min(width - halfWidth - 4, Math.max(halfWidth + 4, position.x)),
    y: Math.min(height - halfHeight - 4, Math.max(halfHeight + 4, position.y)),
  });
  const collides = (candidate: NodePosition): boolean => {
    for (const placed of positions.values()) {
      if (
        Math.abs(candidate.x - placed.x) < nodeWidth + gap &&
        Math.abs(candidate.y - placed.y) < nodeHeight + gap
      ) {
        return true;
      }
    }
    return false;
  };

  for (const item of items) {
    const desired = clamp(source.get(item.node.id) ?? {
      x: width / 2,
      y: height / 2,
    });
    let chosen: NodePosition | undefined;
    const phase =
      ([...item.node.id].reduce((sum, character) => sum + character.charCodeAt(0), 0) %
        24) *
      (Math.PI / 12);
    for (let radius = 0; radius <= Math.max(width, height) && !chosen; radius += 18) {
      const samples = radius === 0 ? 1 : 24;
      for (let sample = 0; sample < samples; sample += 1) {
        const angle = phase + (Math.PI * 2 * sample) / samples;
        const candidate = clamp({
          x: desired.x + Math.cos(angle) * radius,
          y: desired.y + Math.sin(angle) * radius,
        });
        const overlapsCore =
          item.state !== "selected" &&
          Math.abs(candidate.x - width / 2) < halfWidth + 62 &&
          Math.abs(candidate.y - height / 2) < halfHeight + 62;
        if (!overlapsCore && !collides(candidate)) {
          chosen = candidate;
          break;
        }
      }
    }
    positions.set(item.node.id, chosen ?? desired);
  }

  return positions;
}

export function layoutConstellation(
  classified: ClassifiedNode[],
  width: number,
  height: number,
): Map<string, NodePosition> {
  const safeWidth = Math.max(width, 320);
  const safeHeight = Math.max(height, 480);
  const centerX = safeWidth / 2;
  const centerY = safeHeight / 2;
  const positions = new Map<string, NodePosition>();

  const grouped = new Map<NodeState, ClassifiedNode[]>();
  for (const state of stateOrder) grouped.set(state, []);
  for (const item of classified) grouped.get(item.state)!.push(item);
  for (const group of grouped.values()) {
    group.sort((left, right) => left.node.id.localeCompare(right.node.id, "en"));
  }

  if (safeWidth <= 720) {
    const selected = grouped.get("selected")!;
    const centeredSelection = selected.length <= 4 ? selected : [];
    centeredSelection.forEach((item, index) => {
      const spacing = 112;
      const singleOffset = centeredSelection.length === 1 ? -86 : 0;
      positions.set(item.node.id, {
        x: centerX,
        y:
          centerY +
          (index - (centeredSelection.length - 1) / 2) * spacing +
          singleOffset,
      });
    });

    const sideOrder: NodeState[] = [
      "blocked",
      "review",
      "explore",
      "conditional",
      "unknown",
      "available",
      "selected",
    ];
    const sideItems = sideOrder.flatMap((state) =>
      grouped.get(state)!.filter(
        (item) => !positions.has(item.node.id),
      ),
    );
    const columns = safeWidth >= 480 ? 3 : 2;
    const top = 62;
    const bottom = safeHeight - 62;
    const maximumRows = Math.max(
      1,
      Math.floor((bottom - top) / 106) + 1,
    );
    const rowGap = maximumRows > 1 ? (bottom - top) / (maximumRows - 1) : 0;
    const columnGap = columns > 1 ? (safeWidth - 116) / (columns - 1) : 0;
    const slots: NodePosition[] = [];
    for (let row = 0; row < maximumRows; row += 1) {
      for (let column = 0; column < columns; column += 1) {
        const candidate = {
          x: 58 + column * columnGap,
          y: top + row * rowGap,
        };
        const conflictsWithSelection = [...positions.values()].some(
          (selectedPosition) =>
            Math.abs(candidate.x - selectedPosition.x) < 106 &&
            Math.abs(candidate.y - selectedPosition.y) < 106,
        );
        if (!conflictsWithSelection) slots.push(candidate);
      }
    }
    sideItems.forEach((item, index) => {
      positions.set(item.node.id, slots[index] ?? {
        x: 58 + (index % columns) * columnGap,
        y: top + Math.floor(index / columns) * rowGap,
      });
    });
    return positions;
  }

  const hasBlockedNodes = grouped.get("blocked")!.length > 0;
  const ringConfig: Partial<Record<NodeState, [number, number, number]>> = {
    selected: [0.13, 0.12, 0],
    explore: [hasBlockedNodes ? 0.17 : 0.28, 0.26, 0.35],
    conditional: [hasBlockedNodes ? 0.18 : 0.37, 0.36, -0.2],
    review: [hasBlockedNodes ? 0.29 : 0.4, 0.4, 0.18],
    unknown: [hasBlockedNodes ? 0.18 : 0.43, 0.44, 0.15],
    available: [0.39, 0.4, 0],
  };

  for (const state of stateOrder) {
    if (state === "blocked") continue;
    const group = grouped.get(state)!;
    if (state === "unknown" && hasBlockedNodes && group.length > 8) {
      const outer = group.filter((_, index) => index % 2 === 0);
      const inner = group.filter((_, index) => index % 2 === 1);
      const outerRing = ringPositions(
        outer.length,
        centerX,
        centerY,
        safeWidth * 0.18,
        safeHeight * 0.43,
      );
      const innerRing = ringPositions(
        inner.length,
        centerX,
        centerY,
        safeWidth * 0.27,
        safeHeight * 0.27,
        -Math.PI / 2 + Math.PI / inner.length,
      );
      outer.forEach((item, index) => {
        positions.set(item.node.id, outerRing[index]!);
      });
      inner.forEach((item, index) => {
        positions.set(item.node.id, innerRing[index]!);
      });
      continue;
    }
    const config = ringConfig[state]!;
    const [xFactor, yFactor, phase] = config;
    const radiusX = Math.max(54, safeWidth * xFactor);
    const radiusY = Math.max(54, safeHeight * yFactor);
    const ring = ringPositions(
      group.length,
      centerX,
      centerY,
      radiusX,
      radiusY,
      -Math.PI / 2 + phase,
    );
    group.forEach((item, index) => {
      positions.set(item.node.id, ring[index]!);
    });
  }

  const blocked = grouped.get("blocked")!;
  const left = blocked.filter((_, index) => index % 2 === 0);
  const right = blocked.filter((_, index) => index % 2 === 1);
  const sidePositions = (items: ClassifiedNode[], x: number): void => {
    items.forEach((item, index) => {
      const fraction = (index + 1) / (items.length + 1);
      positions.set(item.node.id, {
        x,
        y: 64 + fraction * (safeHeight - 128),
      });
    });
  };
  sidePositions(left, Math.max(62, safeWidth * 0.08));
  sidePositions(right, Math.min(safeWidth - 62, safeWidth * 0.92));

  return resolveCollisions(classified, positions, safeWidth, safeHeight);
}
